import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Params, Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {merge, Observable} from 'rxjs';
import {debounceTime, filter, finalize, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../../destroyable.component';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {DefaultPagination, Pagination} from '../../../../shared/ui';
import {buildFiltersQuery, buildQueryParams, filtersAreDifferent} from '../../../../shared/utils/objects';
import {Request, RequestStatus} from '../../../../shared/workflow/workflow-v2.interface';
import {requests_per_page, WorkflowV2Service} from '../../../../shared/workflow/workflow-v2.service';


@Component({
  selector: 'gp-requests-in-progress-table',
  templateUrl: './requests-in-progress-table.component.html',
  styleUrls: ['../../workflows-common.component.scss',
    '../../workflows.component.scss',
    './requests-in-progress-table.component.scss']
})
export class RequestsInProgressTableComponent extends Destroyable implements OnInit {

  public trigger$: Observable<number>;
  public requests: Request[] = [];
  public loading: boolean;

  public pagination: Pagination = new DefaultPagination(requests_per_page);
  public filterFormGroup: UntypedFormGroup = this.fb.group({
    type: [''],
    subject: [''],
    status: [''],
    assignee: ['']
  });
  public workflowTypes: { key: string }[];
  public WORKFLOW_STATUS = [
    {name: 'WORKFLOWS.in_progress', key: RequestStatus.IN_PROGRESS},
    {name: 'WORKFLOWS.validated', key: RequestStatus.VALIDATED},
    {name: 'WORKFLOWS.rejected', key: RequestStatus.REJECTED},
    {name: 'WORKFLOWS.canceled', key: RequestStatus.CANCELED}
  ];

  constructor(private workflowService: WorkflowV2Service,
              private fb: UntypedFormBuilder,
              public translate: TranslateService,
              private localization: LocalizationService,
              private activatedRoute: ActivatedRoute,
              private router: Router) {
    super();
    this.trigger$ = merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeLanguageEvent
    ).pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );
  }

  ngOnInit() {
    this.workflowService.getWorkflowTypes().pipe(
      tap((types: string[]) => this.workflowTypes = types.map(type => ({key: type})))
    ).subscribe();


    this.activatedRoute.queryParams
      .pipe(
        filter((queryParams: Params) => !!queryParams),
        tap((queryParams: Params) => {
          this.pagination.currentPage.number = queryParams.page || 0;
          if (filtersAreDifferent(this.filterFormGroup.value, queryParams)) {
            this.filterFormGroup.setValue({
              type: queryParams.type || '',
              assignee: queryParams.assignee || '',
              subject: queryParams.subject || '',
              status: queryParams.status || ''
            });
          }
        }),
        switchMap(() => this.getRequestsData()),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();

    this.filterFormGroup.valueChanges
      .pipe(
        debounceTime(400),
        tap(filterValues => this.updateRoute(filterValues)),
        takeUntil(this.componentDestroyed$)
      )
      .subscribe();
  }

  private getRequestsData(): Observable<any> {
    this.loading = true;
    const search = buildFiltersQuery(this.filterFormGroup.value);
    return this.workflowService.getRequests({
      'per-page': this.pagination.perPage,
      page: this.pagination.currentPage.number,
      search
    })
      .pipe(
        tap(response => {
          this.requests = response.data;
          this.pagination.update(response.total);
        }),
        finalize(() => this.loading = false)
      );
  }

  public refreshRequests() {
    this.getRequestsData().subscribe();
  }

  public resetFilters() {
    this.filterFormGroup.reset({type: '', subject: '', status: '', assignee: ''});
  }

  private updateRoute(filterValues: { [key: string]: string }): void {
    this.pagination.currentPage.number = 0;
    const queryParams = buildQueryParams(filterValues);
    this.router.navigate(['inbox/requests'], {queryParams});
  }
}
